<div class="keyword-research-test-container">
  <header class="test-header">
    <h1>🔍 Keyword Research Test</h1>
    <p>Test DataForSEO keyword research endpoints</p>
  </header>

  <!-- SERP Preview -->
  <div class="serp-preview-section">
    <h2>SERP Preview</h2>
    <p class="section-description">Fetch the top 10 Google organic results for a keyword (DataForSEO live SERP)</p>

    <div class="test-form serp-preview-form">
      <div class="field">
        <label class="field__label" for="serp-keyword-input">Keyword:</label>
        <input
          id="serp-keyword-input"
          type="text"
          [(ngModel)]="serpKeyword"
          placeholder="e.g., website feedback tools"
          (keyup.enter)="fetchSerpPreview()"
          class="input"
        />
      </div>

      <div class="field">
        <label class="field__label" for="serp-locale-select">Locale:</label>
        <select id="serp-locale-select" [(ngModel)]="serpLocaleId" class="input">
          <option *ngFor="let locale of serpLocales" [value]="locale.id">
            {{ locale.label }}
          </option>
        </select>
      </div>

      <button
        (click)="fetchSerpPreview()"
        [disabled]="serpLoading || !serpKeyword.trim()"
        class="btn btn--accent"
      >
        {{ serpLoading ? 'Fetching SERP...' : 'Get Top 10 SERP' }}
      </button>

      <div *ngIf="serpError" class="error-message">{{ serpError }}</div>
    </div>

    <div *ngIf="serpResults.length > 0 && !serpLoading" class="google-serp">
      <div class="google-serp-meta">
        <span>Results for “{{ serpQueriedKeyword }}” · {{ selectedSerpLocale.label }}</span>
        <span *ngIf="serpCost > 0">Cost: ${{ serpCost.toFixed(4) }}</span>
      </div>

      <div class="google-serp-results">
        <article *ngFor="let result of serpResults" class="google-result">
          <div class="google-result-cite">
            <span class="google-result-favicon" aria-hidden="true">{{ result.domain.charAt(0) | uppercase }}</span>
            <div class="google-result-cite-text">
              <div class="google-result-domain">{{ result.domain }}</div>
              <div class="google-result-url">{{ displaySerpUrl(result.url) }}</div>
            </div>
          </div>
          <a
            class="google-result-title"
            [href]="result.url"
            target="_blank"
            rel="noopener noreferrer"
          >{{ result.title }}</a>
          <p class="google-result-snippet" *ngIf="result.description">
            <span *ngIf="result.publishedAt" class="google-result-date">
              {{ result.publishedAt | date:'MMM d, yyyy' }} — 
            </span>
            <ng-container *ngFor="let seg of serpSnippetSegments(result)">
              <em *ngIf="seg.emphasized; else serpPlain">{{ seg.text }}</em>
              <ng-template #serpPlain>{{ seg.text }}</ng-template>
            </ng-container>
          </p>
        </article>
      </div>
    </div>
  </div>

  <!-- NEW SECTION: Topic-based batch search -->
  <div class="batch-search-section">
    <h2>Topic Entity Discovery</h2>
    <p class="section-description">Enter a domain and topic to discover keywords for all merged + related topics</p>
    
    <div class="test-form">
      <div class="field">
        <label class="field__label" for="domain-input">Domain:</label>
        <input
          id="domain-input"
          type="text"
          [(ngModel)]="domain"
          placeholder="e.g., marker.io"
          class="input"
        />
      </div>

      <div class="field">
        <label class="field__label" for="topic-slug-input">Topic Slug:</label>
        <input
          id="topic-slug-input"
          type="text"
          [(ngModel)]="topicSlug"
          placeholder="e.g., website-feedback"
          class="input"
        />
      </div>

      <div class="field">
        <label class="field__label" for="batch-location-input">Location Code:</label>
        <input
          id="batch-location-input"
          type="number"
          [(ngModel)]="locationCode"
          class="input"
        />
        <small class="field__hint">2840 = United States</small>
      </div>

      <div class="field">
        <label class="field__label" for="batch-language-input">Language:</label>
        <input
          id="batch-language-input"
          type="text"
          [(ngModel)]="languageName"
          class="input"
        />
      </div>

      <div class="field">
        <label class="field__label" for="batch-depth-input">Depth (0-4):</label>
        <input
          id="batch-depth-input"
          type="number"
          [(ngModel)]="depth"
          min="0"
          max="4"
          class="input"
        />
      </div>

      <div class="field">
        <label class="field__label" for="batch-limit-input">Limit per topic:</label>
        <input
          id="batch-limit-input"
          type="number"
          [(ngModel)]="limit"
          min="1"
          max="1000"
          class="input"
        />
      </div>

      <div class="field">
        <label class="checkbox-label">
          <input
            type="checkbox"
            [(ngModel)]="filterBySearchVolume"
          />
          Filter by minimum search volume
        </label>
        <input
          *ngIf="filterBySearchVolume"
          type="number"
          [(ngModel)]="minSearchVolume"
          min="0"
          class="input"
          style="margin-top: 0.5rem;"
        />
      </div>

      <button 
        (click)="searchTopicsForKeywords()" 
        [disabled]="batchLoading || !domain.trim() || !topicSlug.trim()"
        class="btn btn--accent"
      >
        {{ batchLoading ? 'Searching...' : 'Discover Keywords for All Topics' }}
      </button>

      <div *ngIf="batchError" class="error-message">{{ batchError }}</div>
    </div>

    <!-- Batch Results -->
    <div *ngIf="batchResults.length > 0 && !batchLoading" class="batch-results">
      <div class="results-header">
        <h3>Discovery Results</h3>
        <div class="results-meta">
          <span>Topics Analyzed: {{ batchResults.length }}</span>
          <span>Unique Keywords: {{ getTotalUniqueKeywords() }}</span>
          <span *ngIf="clusteredKeywords.length > 0">
            Clusters: {{ filteredClusteredKeywords.length }}<span *ngIf="clusteredKeywords.length > filteredClusteredKeywords.length">/{{ clusteredKeywords.length }}</span>
          </span>
          <span>Total Cost: ${{ totalCost.toFixed(4) }}</span>
          <span *ngIf="rankedKeywordsMetadata" class="ranked-keywords-info">
            Ranked Keywords: {{ rankedKeywordsMetadata.keywordCount }} 
            <span [class.badge--success]="rankedKeywordsMetadata.dataSource === 'database'" 
                  [class.badge--accent]="rankedKeywordsMetadata.dataSource === 'api'"
                  class="badge">
              ({{ rankedKeywordsMetadata.dataSource === 'database' ? 'DB' : 'API' }})
            </span>
            <span *ngIf="rankedKeywordsMetadata.lastUpdated" class="last-updated">
              • Updated: {{ rankedKeywordsMetadata.lastUpdated | date:'short' }}
            </span>
          </span>
        </div>
        <div class="view-toggle" *ngIf="clusteredKeywords.length > 0">
          <button 
            (click)="showClusteredView = false" 
            [class.btn--accent]="!showClusteredView"
            [class.btn--quiet]="showClusteredView"
            class="btn btn--sm"
          >
            Topic View
          </button>
          <button 
            (click)="showClusteredView = true" 
            [class.btn--accent]="showClusteredView"
            [class.btn--quiet]="!showClusteredView"
            class="btn btn--sm"
          >
            Clustered View
          </button>
        </div>
      </div>

      <!-- Clustered View -->
      <div *ngIf="showClusteredView && filteredClusteredKeywords.length > 0" class="clustered-view">
        <div class="clustered-results-info" *ngIf="clusteredKeywords.length > filteredClusteredKeywords.length">
          <small>Showing {{ filteredClusteredKeywords.length }} of {{ clusteredKeywords.length }} clusters (similarity ≥ {{ (similarityThreshold * 100).toFixed(0) }}%)</small>
        </div>
        <div class="clustered-results">
          <div *ngFor="let cluster of filteredClusteredKeywords" class="cluster-wrapper">
            <span 
              class="badge keyword-tag cluster-header"
              [class.badge--success]="cluster.isCovered"
              [class.badge--danger]="!cluster.isCovered"
              [class.expanded]="isClusterExpanded(cluster.clusterName)"
              (click)="toggleClusterExpansion(cluster.clusterName)"
              [title]="cluster.totalKeywordVariants + ' keyword variants' + (cluster.totalUrlCount > 0 ? ', ' + cluster.totalUrlCount + ' URLs' : '') + (cluster.similarityScore !== undefined ? ', Similarity: ' + (cluster.similarityScore * 100).toFixed(0) + '%' : '') + ' - Click to expand'"
            >
              {{ cluster.clusterName }}
              <span *ngIf="cluster.isCovered && cluster.totalUrlCount > 0" class="url-count">({{ cluster.totalUrlCount }})</span>
              <span *ngIf="cluster.similarityScore !== undefined" 
                    class="similarity-score"
                    [class.high-similarity]="cluster.similarityScore >= 0.7"
                    [class.medium-similarity]="cluster.similarityScore >= 0.4 && cluster.similarityScore < 0.7"
                    [class.low-similarity]="cluster.similarityScore < 0.4">
                {{ (cluster.similarityScore * 100).toFixed(0) }}%
              </span>
              <span class="expand-icon">{{ isClusterExpanded(cluster.clusterName) ? '▼' : '▶' }}</span>
            </span>
            <div *ngIf="isClusterExpanded(cluster.clusterName)" class="expanded-cluster">
              <div *ngFor="let dedupedKw of cluster.keywords" class="deduplicated-keyword">
                <span 
                  class="badge keyword-tag canonical-keyword"
                  [class.badge--success]="dedupedKw.isCovered"
                  [class.badge--danger]="!dedupedKw.isCovered"
                  [title]="'Canonical: ' + dedupedKw.canonicalKeyword + ', Search Volume: ' + dedupedKw.totalSearchVolume + (dedupedKw.isCovered ? (dedupedKw.bestRankingPosition ? ', Ranking: #' + dedupedKw.bestRankingPosition : ', URLs: ' + dedupedKw.totalUrlCount) : '')"
                >
                  {{ dedupedKw.canonicalKeyword }}
                  <span *ngIf="dedupedKw.isCovered && dedupedKw.bestRankingPosition" class="badge badge--success">#{{ dedupedKw.bestRankingPosition }}</span>
                  <span *ngIf="dedupedKw.isCovered && dedupedKw.totalUrlCount && !dedupedKw.bestRankingPosition" class="url-count">({{ dedupedKw.totalUrlCount }})</span>
                  <span class="variant-count">({{ dedupedKw.variants.length }} variant{{ dedupedKw.variants.length !== 1 ? 's' : '' }})</span>
                </span>
                <div class="keyword-variants">
                  <span 
                    *ngFor="let variant of dedupedKw.variants" 
                    class="badge keyword-tag variant-keyword"
                    [class.badge--success]="variant.isCovered"
                    [class.badge--danger]="!variant.isCovered"
                    [title]="'Search Volume: ' + variant.search_volume + ', CPC: $' + variant.cpc + (variant.isCovered ? (variant.rankingPosition ? ', Ranking: #' + variant.rankingPosition : ', URLs: ' + (variant.urlCount || 0)) : '')"
                  >
                    {{ variant.keyword }}
                    <span *ngIf="variant.isCovered && variant.rankingPosition" class="badge badge--success">#{{ variant.rankingPosition }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Topic View (existing) -->
      <div *ngIf="!showClusteredView" class="topic-view">
      <div class="topic-results" *ngFor="let topicResult of batchResults">
        <div class="topic-header">
          <h4>{{ topicResult.topicLabel }}</h4>
          <span class="topic-type">{{ topicResult.topicType }}</span>
        </div>
        
        <div class="keyword-sections">
          <div class="keyword-section" *ngIf="topicResult.groupedRelatedKeywords.length > 0">
            <h5>
              Related Keywords ({{ topicResult.groupedRelatedKeywords.length }} groups)
              <span *ngIf="topicResult.relatedKeywordsMetadata" class="data-source-info">
                <span [class.badge--success]="topicResult.relatedKeywordsMetadata.dataSource === 'database'" 
                      [class.badge--accent]="topicResult.relatedKeywordsMetadata.dataSource === 'api'"
                      class="badge">
                  ({{ topicResult.relatedKeywordsMetadata.dataSource === 'database' ? 'DB' : 'API' }})
                </span>
              </span>
            </h5>
            <div class="keyword-groups">
              <div *ngFor="let group of topicResult.groupedRelatedKeywords" class="keyword-group-wrapper">
                <span 
                  class="badge keyword-tag group-header"
                  [class.badge--success]="group.isCovered"
                  [class.badge--danger]="!group.isCovered"
                  [class.expanded]="isGroupExpanded(topicResult.topicSlug, 'related', group.groupName)"
                  (click)="toggleGroupExpansion(topicResult.topicSlug, 'related', group.groupName)"
                  [title]="group.totalCount + ' keywords' + (group.totalUrlCount > 0 ? ', ' + group.totalUrlCount + ' URLs' : '') + ' - Click to expand'"
                >
                  {{ group.groupName }}
                  <span *ngIf="group.isCovered && group.totalUrlCount > 0" class="url-count">({{ group.totalUrlCount }})</span>
                  <span class="expand-icon">{{ isGroupExpanded(topicResult.topicSlug, 'related', group.groupName) ? '▼' : '▶' }}</span>
                </span>
                <div *ngIf="isGroupExpanded(topicResult.topicSlug, 'related', group.groupName)" class="expanded-keywords">
                  <span 
                    *ngFor="let kw of group.keywords" 
                    class="badge keyword-tag expanded-keyword"
                    [class.badge--success]="kw.isCovered"
                    [class.badge--danger]="!kw.isCovered"
                    [title]="'Search Volume: ' + kw.search_volume + ', CPC: $' + kw.cpc + (kw.isCovered ? (kw.rankingPosition ? ', Ranking: #' + kw.rankingPosition + (kw.rankingUrl ? ' (' + kw.rankingUrl + ')' : '') : ', URLs: ' + kw.urlCount) : '')"
                  >
                    {{ kw.keyword }}
                    <span *ngIf="kw.isCovered && kw.rankingPosition" class="badge badge--success">#{{ kw.rankingPosition }}</span>
                    <span *ngIf="kw.isCovered && kw.urlCount && !kw.rankingPosition" class="url-count">({{ kw.urlCount }})</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="keyword-section" *ngIf="topicResult.groupedSuggestions.length > 0">
            <h5>
              Keyword Suggestions ({{ topicResult.groupedSuggestions.length }} groups)
              <span *ngIf="topicResult.suggestionsMetadata" class="data-source-info">
                <span [class.badge--success]="topicResult.suggestionsMetadata.dataSource === 'database'" 
                      [class.badge--accent]="topicResult.suggestionsMetadata.dataSource === 'api'"
                      class="badge">
                  ({{ topicResult.suggestionsMetadata.dataSource === 'database' ? 'DB' : 'API' }})
                </span>
              </span>
            </h5>
            <div class="keyword-groups">
              <div *ngFor="let group of topicResult.groupedSuggestions" class="keyword-group-wrapper">
                <span 
                  class="badge keyword-tag group-header"
                  [class.badge--success]="group.isCovered"
                  [class.badge--danger]="!group.isCovered"
                  [class.expanded]="isGroupExpanded(topicResult.topicSlug, 'suggestions', group.groupName)"
                  (click)="toggleGroupExpansion(topicResult.topicSlug, 'suggestions', group.groupName)"
                  [title]="group.totalCount + ' keywords' + (group.totalUrlCount > 0 ? ', ' + group.totalUrlCount + ' URLs' : '') + ' - Click to expand'"
                >
                  {{ group.groupName }}
                  <span *ngIf="group.isCovered && group.totalUrlCount > 0" class="url-count">({{ group.totalUrlCount }})</span>
                  <span class="expand-icon">{{ isGroupExpanded(topicResult.topicSlug, 'suggestions', group.groupName) ? '▼' : '▶' }}</span>
                </span>
                <div *ngIf="isGroupExpanded(topicResult.topicSlug, 'suggestions', group.groupName)" class="expanded-keywords">
                  <span 
                    *ngFor="let kw of group.keywords" 
                    class="badge keyword-tag expanded-keyword"
                    [class.badge--success]="kw.isCovered"
                    [class.badge--danger]="!kw.isCovered"
                    [title]="'Search Volume: ' + kw.search_volume + ', CPC: $' + kw.cpc + (kw.isCovered ? (kw.rankingPosition ? ', Ranking: #' + kw.rankingPosition + (kw.rankingUrl ? ' (' + kw.rankingUrl + ')' : '') : ', URLs: ' + kw.urlCount) : '')"
                  >
                    {{ kw.keyword }}
                    <span *ngIf="kw.isCovered && kw.rankingPosition" class="badge badge--success">#{{ kw.rankingPosition }}</span>
                    <span *ngIf="kw.isCovered && kw.urlCount && !kw.rankingPosition" class="url-count">({{ kw.urlCount }})</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>

  <!-- EXISTING SECTION: Single keyword search -->
  <div class="single-search-section">
    <h2>Single Keyword Search</h2>
    <p class="section-description">Test individual keyword research endpoints</p>
    
    <div class="test-form">
    <div class="field">
      <label class="field__label" for="keyword-input">{{ method === 'keywords-for-site' ? 'Domain:' : 'Keyword:' }}</label>
      <input
        id="keyword-input"
        type="text"
        [(ngModel)]="keyword"
        [placeholder]="method === 'keywords-for-site' ? 'e.g., marker.io' : 'e.g., website feedback'"
        (keyup.enter)="testKeywordResearch()"
        class="input"
      />
      <small class="field__hint" *ngIf="method === 'keywords-for-site'">Enter domain name without https://</small>
    </div>

    <div class="field">
      <label class="field__label" for="method-select">Method:</label>
      <select id="method-select" [(ngModel)]="method" class="input">
        <option value="related">Related Keywords</option>
        <option value="ideas">Keyword Ideas</option>
        <option value="suggestions">Keyword Suggestions</option>
        <option value="keywords-for-site">Keywords For Site</option>
      </select>
    </div>

    <div class="field" *ngIf="method === 'related'">
      <label class="field__label" for="depth-input">Depth (0-4):</label>
      <input
        id="depth-input"
        type="number"
        [(ngModel)]="depth"
        min="0"
        max="4"
        class="input"
      />
      <small class="field__hint">Higher depth = more keywords (up to 4680)</small>
    </div>

    <div class="field">
      <label class="field__label" for="limit-input">Limit:</label>
      <input
        id="limit-input"
        type="number"
        [(ngModel)]="limit"
        min="1"
        max="1000"
        class="input"
      />
    </div>

    <div class="field">
      <label class="field__label" for="location-input">Location Code:</label>
      <input
        id="location-input"
        type="number"
        [(ngModel)]="locationCode"
        class="input"
      />
      <small class="field__hint">2840 = United States</small>
    </div>

    <div class="field">
      <label class="field__label" for="language-input">Language:</label>
      <input
        id="language-input"
        type="text"
        [(ngModel)]="languageName"
        class="input"
      />
    </div>

    <div class="field">
      <label class="checkbox-label">
        <input
          type="checkbox"
          [(ngModel)]="filterBySearchVolume"
        />
        Filter by minimum search volume
      </label>
      <input
        *ngIf="filterBySearchVolume"
        type="number"
        [(ngModel)]="minSearchVolume"
        min="0"
        class="input"
        style="margin-top: 0.5rem;"
      />
      <small class="field__hint" *ngIf="filterBySearchVolume">Only show keywords with search volume greater than this value</small>
    </div>

    <button 
      (click)="testKeywordResearch()" 
      [disabled]="loading || !keyword.trim()"
      class="btn btn--accent"
    >
      {{ loading ? 'Loading...' : 'Test Keyword Research' }}
    </button>

    <div *ngIf="error" class="error-message">{{ error }}</div>
  </div>

  <div *ngIf="results && !loading" class="results-section">
    <div class="results-header">
      <h2>Results</h2>
      <div class="results-meta">
        <span>Total: {{ getTotalCount() }}</span>
        <span>Returned: {{ getItemsCount() }}</span>
        <span>Cost: ${{ getCost().toFixed(4) }}</span>
        <span *ngIf="resultsMetadata" class="data-source-info">
          Data Source: 
          <span [class.badge--success]="resultsMetadata.dataSource === 'database'" 
                [class.badge--accent]="resultsMetadata.dataSource === 'api'"
                class="badge">
            {{ resultsMetadata.dataSource === 'database' ? 'Keyword Research Test Database' : (resultsMetadata.dataSource === 'api' ? 'API' : 'Unknown') }}
          </span>
          <span *ngIf="resultsMetadata.lastUpdated" class="last-updated">
            • Updated: {{ resultsMetadata.lastUpdated | date:'short' }}
          </span>
        </span>
      </div>
    </div>

    <div class="results-table">
      <table class="table">
        <thead>
          <tr>
            <th>Keyword</th>
            <th>Search Volume</th>
            <th>Competition</th>
            <th>Competition Level</th>
            <th>CPC</th>
            <th>Keyword Difficulty</th>
            <th>Categories</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let keyword of getKeywordsFromResults()">
            <td class="keyword-cell">{{ keyword.keyword }}</td>
            <td>{{ keyword.search_volume.toLocaleString() }}</td>
            <td>{{ (keyword.competition * 100).toFixed(1) }}%</td>
            <td>
              <span [class]="'competition-' + keyword.competition_level.toLowerCase()">
                {{ keyword.competition_level }}
              </span>
            </td>
            <td>${{ keyword.cpc.toFixed(2) }}</td>
            <td>{{ keyword.keyword_difficulty !== undefined ? keyword.keyword_difficulty : 'N/A' }}</td>
            <td>{{ keyword.categories?.length || 0 }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="raw-results">
      <details>
        <summary>Raw API Response</summary>
        <pre>{{ results | json }}</pre>
      </details>
    </div>
  </div>
  </div>
</div>

